import Emitter from 'component-emitter';
import { HubConnection } from '@microsoft/signalr';

import { webSockets } from 'app/packages/lib';
import type { Nullable } from 'app/types/tsHelpers';

import { BalanceConnection } from './connections/balance';
import { BetSlipConnection } from './connections/bet-slip';
import { CashbackExperienceConnection } from './connections/cashback';
import { NotificationsConnection } from './connections/notifications';
import { StakesConnection } from './connections/stakes';

// ---

export type SubscriptionsExtArgs = webSockets.SubscriptionsExtArgs;

// ---

type Hubs = Nullable<{
  subscribe: webSockets.SubscribeHub;
  subscribeExt: webSockets.SubscribeExtHub;
  subscribeFavorites: webSockets.SubscribeFavoritesHub;
}>;

class MainHub {
  private readonly _hubs: Hubs = {
    subscribe: null,
    subscribeExt: null,
    subscribeFavorites: null,
  };

  public readonly emmiter = new Emitter;
  private _hubConnection: HubConnection | null = null;

  public initialize = (hubConnection: HubConnection, isBackendApiV2: boolean) => {
    this._hubs.subscribe = new webSockets.SubscribeHub(hubConnection, isBackendApiV2);
    this._hubs.subscribeExt = new webSockets.SubscribeExtHub(hubConnection, isBackendApiV2);
    this._hubs.subscribeFavorites = new webSockets.SubscribeFavoritesHub(hubConnection, false);
    this._hubConnection = hubConnection;

    new NotificationsConnection(hubConnection, this.emmiter);
    new StakesConnection(hubConnection, this.emmiter);
    new BetSlipConnection(hubConnection, this.emmiter);
    new BalanceConnection(hubConnection, this.emmiter);
    new CashbackExperienceConnection(hubConnection, this.emmiter);
  };

  public tryToGetHub = (): HubConnection | never => {
    if (this._hubConnection === null) {
      throw new Error('MainHub -> first you need to cause MainHub.initialize function');
    }

    return this._hubConnection;
  };

  public tryToGetHubConnection = <T extends keyof Hubs>(hubName: T) => {
    const hub = this._hubs[hubName];

    if (hub === null) {
      throw new Error('MainHub -> hubConnection should not be null.');
    }

    return hub as NonNullable<Hubs[T]>;
  };
}

export default new MainHub();
